import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import * as echarts from 'echarts';
import { LayoutService } from './layout.service';
import {
  Theme,
  DeepPartial,
  ChartTheme,
  TooltipTheme,
  TableTheme,
  ThemeManifest,
  ThemeManifestEntry,
  CustomPreset,
} from '@shared-lib/foundry-shared/theme.types';
import { formatValueSafely } from '../utils/text-case.utils';

// ============ DEEP MERGE ============

/**
 * Recursively merges override objects onto a base object.
 *
 * Rules:
 *   - Nested objects are merged (not replaced).
 *   - Arrays (like chart.palette) are REPLACED entirely, not appended.
 *   - `undefined` values in overrides are skipped (inherit from base).
 *
 * @param base - the base object (e.g. central theme)
 * @param overrides - one or more partial overrides applied left-to-right
 */
function deepMerge<T extends Record<string, any>>(
  base: T,
  ...overrides: (DeepPartial<T> | undefined | null)[]
): T {
  const result: any = Array.isArray(base) ? [...(base as any)] : { ...base };
  for (const override of overrides) {
    if (!override) continue;
    for (const key of Object.keys(override) as (keyof T)[]) {
      const baseVal = result[key];
      const overVal = (override as any)[key];
      if (overVal === undefined) continue;
      if (
        overVal !== null &&
        typeof overVal === 'object' &&
        !Array.isArray(overVal) &&
        baseVal !== null &&
        typeof baseVal === 'object' &&
        !Array.isArray(baseVal)
      ) {
        result[key] = deepMerge(baseVal, overVal);
      } else {
        result[key] = overVal;
      }
    }
  }
  return result as T;
}

// ============ SERVICE ============

/** LocalStorage keys for persisted user theme selection + custom overrides. */
const STORAGE_KEY_ACTIVE_THEME = 'dvm.theme.activeName';
const STORAGE_KEY_CUSTOM_OVERRIDES = 'dvm.theme.customOverrides';
/** LocalStorage key for the user's named custom presets (Custom 1, Custom 2…). */
const STORAGE_KEY_CUSTOM_PRESETS = 'dvm.theme.customPresets';
const STORAGE_KEY_ACTIVE_PRESET_ID = 'dvm.theme.activePresetId';

/**
 * Tokens that are intentionally FIXED across templates.
 * Switching a template (Premium Gold, Executive Dark, etc.) should not change:
 *   - chart series palette (chart colors stay branded)
 *   - table HEADER colors (navy + white — brand identity)
 *   - stat card numeric / label colors (so KPI values don't turn gold etc.)
 *
 * Row bgs, content bgs, card bgs etc. still flip with the template so the
 * dashboard "feel" reacts to dark/light. Dashboard-level and widget-level
 * `themeOverrides.*` still win because they merge AFTER these locked values.
 */
const FIXED_CHART_PALETTE: string[] = [
  '#043D63', '#DA291C', '#2563eb', '#059669', '#d97706',
  '#7c3aed', '#0891b2', '#be123c', '#4f46e5', '#0d9488',
  '#c2410c', '#7e22ce', '#0369a1', '#b91c1c', '#15803d',
  '#a16207', '#6d28d9', '#0e7490', '#9f1239', '#1d4ed8',
];

const FIXED_TABLE_HEADER = {
  headerBg: '#2F8DF4',
  headerText: '#ffffff',
  rowBg: '#ffffff',
  altRowBg: '#f9fafb',
  hoverBg: '#f3f4f6',
  borderColor: 'rgba(0, 0, 0, 0.2)',
  commonBg: '#eff6ff',
  nonCommonBg: '#f9fafb',
  frozenBg: '#ffffff',
  frozenBorderColor: 'rgba(0, 0, 0, 0.2)',
};

const FIXED_STAT_CARD = {
  bg: '#ffffff',
  titleColor: '#505050',
  valueColor: '#0A0A0A',
  borderColor: 'rgba(0, 0, 0, 0.2)',
  shadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
};

/**
 * Detect whether the current theme is a "dark" theme by checking the
 * perceived brightness of its content background. Used so we don't force
 * white table/stat-card backgrounds on top of dark themes.
 */
function isDarkTheme(theme: Theme): boolean {
  const bg = theme.layout?.content?.bg;
  if (typeof bg !== 'string') return false;
  const hex = bg.replace('#', '').slice(0, 6);
  if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) return false;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const brightness = r * 0.299 + g * 0.587 + b * 0.114;
  return brightness < 128;
}

/**
 * Overwrite the central theme's locked tokens with their fixed defaults.
 *
 * Always locked (every template): chart palette, table HEADER (navy + white).
 * Light templates: also lock table row bgs + stat-card to default light values
 *   so Premium Gold / Soft Blue don't tint rows or numbers.
 * Dark templates: leave row bgs / stat-card alone so the dashboard looks
 *   actually dark (no white islands).
 */
function lockChartPalette(theme: Theme | null): Theme | null {
  if (!theme) return theme;
  const dark = isDarkTheme(theme);
  const chart = theme.chart ? { ...theme.chart, palette: FIXED_CHART_PALETTE } : theme.chart;
  const table = theme.table
    ? (dark
        // dark theme: only force the brand header — keep dark row bgs from the theme
        ? { ...theme.table, headerBg: FIXED_TABLE_HEADER.headerBg, headerText: FIXED_TABLE_HEADER.headerText }
        : { ...theme.table, ...FIXED_TABLE_HEADER })
    : theme.table;
  const statCard = theme.statCard
    ? (dark ? theme.statCard : { ...theme.statCard, ...FIXED_STAT_CARD })
    : theme.statCard;
  return { ...theme, chart, table, statCard };
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** The loaded centralized theme (source of truth). */
  private centralTheme: Theme | null = null;

  /** Dashboard-level overrides (set per dashboard, cleared on navigation). */
  private dashboardOverrides: DeepPartial<Theme> | null = null;

  /** User-level custom overrides (from Custom Theme tab — persisted in localStorage). */
  private customOverrides: DeepPartial<Theme> | null = null;

  /** Cache of loaded theme JSON files keyed by name. */
  private themeCache = new Map<string, Theme>();

  /** Cached theme manifest (list of selectable themes). */
  private manifestCache: ThemeManifest | null = null;

  /** Observable that emits whenever the effective global theme changes. */
  private themeSubject = new BehaviorSubject<Theme | null>(null);
  theme$ = this.themeSubject.asObservable();

  /** Observable that emits the manifest once loaded. */
  private manifestSubject = new BehaviorSubject<ThemeManifest | null>(null);
  manifest$ = this.manifestSubject.asObservable();

  /**
   * True while a previewed (Apply & Preview) theme is live but not yet saved.
   * Top-bar "Save Theme" button subscribes to this to show/hide itself.
   */
  private pendingPreviewSubject = new BehaviorSubject<boolean>(false);
  pendingPreview$ = this.pendingPreviewSubject.asObservable();

  /** Name of the currently active centralized theme. */
  currentThemeName = 'default';

  /**
   * Id of the currently active custom preset (Custom 1, Custom 2, …) if one
   * is in effect, else `null`. Lets the Theme Selector dialog highlight the
   * correct card on reopen, and survives reload via localStorage.
   */
  activePresetId: string | null = null;

  /**
   * In-memory custom preset that has been previewed but not yet committed to
   * localStorage. Set by the Theme Selector's "Apply & Preview" path so the
   * preset isn't persisted until the user clicks the toolbar Save Theme
   * button. `commitPendingPreview()` writes it to the saved presets list and
   * clears this field; discard/reset paths also clear it.
   */
  pendingCustomPreset: CustomPreset | null = null;

  /** localStorage key used to persist the user's light/dark preference. */
  private static readonly STORAGE_KEY = 'dvm.theme';

  /**
   * LayoutService is injected via the field-init form (not the ctor) so the
   * existing `constructor(private http)` signature stays untouched — adding
   * a second positional dep here would have rippled through every test
   * harness that news-up ThemeService manually. Coupling direction is fine:
   * LayoutService doesn't import ThemeService, so no cycle.
   */
  private layoutService = inject(LayoutService);

  constructor(private http: HttpClient) {}

  private themeScope?: HTMLElement;

  /** Renderer-owned instances apply theme state inside their host only. */
  setThemeScope(target: HTMLElement): void {
    this.themeScope = target;
  }

  // ── User preference (light/dark toggle) ─────────────────────────

  /**
   * Read the user's persisted theme preference (set via the toolbar toggle).
   * Returns `null` if the user has not made a choice yet.
   */
  getUserPreference(): string | null {
    try {
      return localStorage.getItem(ThemeService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Persist the user's theme choice and apply it in-memory (no page reload).
   *
   * `init()` updates CSS variables, re-registers the ECharts 'dvm' theme, and
   * emits on `theme$` — every chart subscribed to `theme$` re-renders under the
   * new palette. On the next page load, the screen reads this key via
   * `getUserPreference()` so the choice survives refreshes.
   */
  setTheme(themeName: string): void {
    try {
      localStorage.setItem(ThemeService.STORAGE_KEY, themeName);
      localStorage.setItem(STORAGE_KEY_ACTIVE_THEME, themeName);
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
    } catch {
      /* storage disabled - swallow */
    }
    this.activePresetId = null;
    this.init(themeName);
  }

  /**
   * Toggle between 'default' (light) and 'dark' themes. Persists the choice.
   */
  toggleTheme(): void {
    const next = this.currentThemeName === 'dark' ? 'default' : 'dark';
    this.setTheme(next);
  }

  // ── Manifest (Theme Registry) ───────────────────────────────────

  /**
   * Load the theme manifest (list of selectable themes) from assets.
   * Cached after first load. Safe to call multiple times.
   */
  loadManifest(): Observable<ThemeManifest> {
    if (this.manifestCache) return of(this.manifestCache);

    return this.http
      .get<ThemeManifest>('assets/theme-config/themes-manifest.json')
      .pipe(
        tap((manifest) => {
          this.manifestCache = manifest;
          this.manifestSubject.next(manifest);
        }),
        catchError((err) => {
          console.warn('[ThemeService] Failed to load theme manifest.', err);
          const fallback: ThemeManifest = {
            defaultTheme: 'default',
            themes: [
              {
                id: 'default',
                name: 'Base Light',
                type: 'light',
                preview: { primary: '#043D63', surface: '#ffffff', accent: '#DA291C' },
              },
            ],
          };
          this.manifestCache = fallback;
          this.manifestSubject.next(fallback);
          return of(fallback);
        }),
      );
  }

  /** Synchronous accessor — returns the cached manifest or null if not loaded yet. */
  getManifest(): ThemeManifest | null {
    return this.manifestCache;
  }

  // ── Persistence (LocalStorage) ──────────────────────────────────

  /** Read the saved active theme id from localStorage. */
  getSavedThemeName(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_ACTIVE_THEME);
    } catch {
      return null;
    }
  }

  /** Read saved custom overrides (from Custom Theme tab) from localStorage. */
  getSavedCustomOverrides(): DeepPartial<Theme> | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CUSTOM_OVERRIDES);
      return raw ? (JSON.parse(raw) as DeepPartial<Theme>) : null;
    } catch {
      return null;
    }
  }

  // ── Initialization ──────────────────────────────────────────────

  /**
   * Load a theme JSON and apply it as the centralized theme.
   * Use this when you want async load + apply in one call.
   *
   * Call once per dashboard load (or on app init for a global theme).
   */
  init(themeName = 'default'): void {
    if (this.themeCache.has(themeName)) {
      this.centralTheme = this.themeCache.get(themeName)!;
      this.currentThemeName = themeName;
      this.applyGlobalTheme();
      return;
    }

    this.loadTheme(themeName).subscribe((theme) => {
      this.centralTheme = theme;
      this.currentThemeName = themeName;
      this.themeCache.set(themeName, theme);
      this.applyGlobalTheme();
    });
  }

  /**
   * Apply an already-loaded theme (avoids a second HTTP request).
   * Use this when the caller pre-loaded the theme via loadTheme() to control timing —
   * Screens should load the theme before rendering widgets.
   */
  initFromLoaded(themeName: string, theme: Theme): void {
    this.centralTheme = theme;
    this.currentThemeName = themeName;
    this.themeCache.set(themeName, theme);
    this.applyGlobalTheme();
  }

  /**
   * Load a theme JSON from assets with fallback to default on failure.
   * Returns an Observable so callers can chain via switchMap for timing control.
   */
  loadTheme(themeName: string): Observable<Theme> {
    // Return cached version if available (no HTTP call).
    if (this.themeCache.has(themeName)) {
      return of(this.themeCache.get(themeName)!);
    }

    const url = `assets/theme-config/${themeName}-theme.json`;
    return this.http.get<Theme>(url).pipe(
      tap((theme) => this.themeCache.set(themeName, theme)),
      catchError((err) => {
        console.warn(
          `[ThemeService] Failed to load theme "${themeName}", falling back to default.`,
          err,
        );
        if (themeName !== 'default') {
          return this.themeCache.has('default')
            ? of(this.themeCache.get('default')!)
            : this.http.get<Theme>('assets/theme-config/default-theme.json').pipe(
                tap((theme) => this.themeCache.set('default', theme)),
              );
        }
        // Default also failed — return an empty stub to avoid crashes.
        return of({} as Theme);
      }),
    );
  }

  // ── User Theme Selection (Preview / Save / Reset) ───────────────

  /**
   * Boot-time initialization that checks localStorage first.
   * Resolution order: saved theme (if any) → fallback name → 'default'.
   * Also reapplies any saved custom overrides on top.
   */
  initFromPersistence(fallbackName = 'default'): void {
    const savedCustom = this.getSavedCustomOverrides();
    if (savedCustom) this.customOverrides = savedCustom;
    this.activePresetId = this.getSavedActivePresetId();

    const savedName = this.getSavedThemeName();
    const nameToLoad = savedName || this.getUserPreference() || fallbackName;
    this.init(nameToLoad);
  }

  /**
   * Temporarily apply a theme without persisting it.
   * Used by the Theme Selector's "Apply & Preview" action.
   */
  previewTheme(themeName: string): void {
    this.init(themeName);
  }

  /**
   * Apply + persist a theme selection to localStorage.
   * Used by the Theme Selector's "Save" action.
   */
  saveTheme(themeName: string): void {
    this.activePresetId = null;
    this.init(themeName);
    try {
      localStorage.setItem(STORAGE_KEY_ACTIVE_THEME, themeName);
      localStorage.setItem(ThemeService.STORAGE_KEY, themeName);
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
    } catch (e) {
      console.warn('[ThemeService] Could not persist theme selection.', e);
    }
  }

  /**
   * Temporarily layer custom overrides (e.g. custom side-nav / top-nav colors)
   * on top of the current central theme without persisting.
   */
  previewCustomOverrides(overrides: DeepPartial<Theme>): void {
    this.customOverrides = overrides;
    this.applyGlobalTheme();
  }

  /**
   * Apply a template live (no persistence) and flag a pending preview so the
   * toolbar Save button shows. Clears any in-memory custom overrides first
   * (matches the Templates-tab Save semantics).
   */
  applyTemplatePreview(themeName: string): void {
    this.customOverrides = null;
    this.activePresetId = null;
    this.init(themeName);
    this.pendingPreviewSubject.next(true);
  }

  /**
   * Apply custom overrides live on top of the active template (no persistence)
   * and flag a pending preview.
   */
  applyCustomOverridesPreview(overrides: DeepPartial<Theme>): void {
    this.customOverrides = overrides;
    this.applyGlobalTheme();
    this.pendingPreviewSubject.next(true);
  }

  // ── Custom Presets (multiple named user-created templates) ──────

  /** Raw read of persisted presets — used by mutators (upsert/delete). */
  private readSavedPresetsFromStorage(): CustomPreset[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CUSTOM_PRESETS);
      return raw ? (JSON.parse(raw) as CustomPreset[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * Read the user's custom presets (Custom 1, Custom 2 …) from localStorage.
   * Returns ONLY committed presets — the in-memory `pendingCustomPreset`
   * (staged via Apply & Preview) is intentionally excluded so the dialog's
   * Custom Templates list does not show an entry for unsaved work. The
   * pending preset becomes a real card only after the toolbar Save Theme
   * triggers `commitPendingPreview()`.
   */
  getCustomPresets(): CustomPreset[] {
    return this.readSavedPresetsFromStorage();
  }

  /** Add a new preset or update an existing one (matched by id). */
  upsertCustomPreset(preset: CustomPreset): void {
    const presets = this.readSavedPresetsFromStorage();
    const idx = presets.findIndex((p) => p.id === preset.id);
    if (idx >= 0) {
      presets[idx] = preset;
    } else {
      presets.push(preset);
    }
    try {
      localStorage.setItem(STORAGE_KEY_CUSTOM_PRESETS, JSON.stringify(presets));
    } catch (e) {
      console.warn('[ThemeService] Could not persist custom presets.', e);
    }
  }

  /** Remove a custom preset by id. */
  deleteCustomPreset(id: string): void {
    const presets = this.readSavedPresetsFromStorage().filter((p) => p.id !== id);
    try {
      localStorage.setItem(STORAGE_KEY_CUSTOM_PRESETS, JSON.stringify(presets));
    } catch {
      /* ignore */
    }
    if (this.pendingCustomPreset?.id === id) this.pendingCustomPreset = null;
    if (this.activePresetId === id) {
      this.activePresetId = null;
      try {
        localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Apply a saved custom preset live: load its base template and layer the
   * preset's overrides on top, then flag a pending preview so the toolbar
   * Save button shows. If the preset bundles a `layoutId`, the layout is
   * applied (or cleared) alongside the theme — this is what lets a custom
   * template carry both a color/typography palette AND a row pattern.
   *
   * `layoutId === undefined` means the preset is layout-agnostic: don't
   * touch the active layout. Distinguishing undefined from null is what
   * makes legacy presets (saved before this field existed) keep whatever
   * layout the user already had on screen.
   */
  applyCustomPresetPreview(preset: CustomPreset): void {
    this.customOverrides = preset.overrides;
    this.activePresetId = preset.id;
    this.init(preset.baseTemplate); // applyGlobalTheme runs inside init
    if (preset.layoutId !== undefined) {
      this.layoutService.applyLayoutPreview(preset.layoutId);
    }
    this.pendingPreviewSubject.next(true);
  }

  /** Read the persisted active-preset id, if any. */
  getSavedActivePresetId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_ACTIVE_PRESET_ID);
    } catch {
      return null;
    }
  }

  /**
   * Persist the currently-applied template + customOverrides to localStorage
   * and clear the pending-preview flag. Called from the toolbar Save button.
   * Also flushes any in-memory `pendingCustomPreset` to the saved presets list
   * so the user's Apply & Preview work is only persisted at this point.
   */
  commitPendingPreview(): void {
    try {
      if (this.pendingCustomPreset) {
        this.upsertCustomPreset(this.pendingCustomPreset);
        this.pendingCustomPreset = null;
      }
      localStorage.setItem(STORAGE_KEY_ACTIVE_THEME, this.currentThemeName);
      localStorage.setItem(ThemeService.STORAGE_KEY, this.currentThemeName);
      if (this.customOverrides) {
        localStorage.setItem(
          STORAGE_KEY_CUSTOM_OVERRIDES,
          JSON.stringify(this.customOverrides),
        );
      } else {
        localStorage.removeItem(STORAGE_KEY_CUSTOM_OVERRIDES);
      }
      if (this.activePresetId) {
        localStorage.setItem(STORAGE_KEY_ACTIVE_PRESET_ID, this.activePresetId);
      } else {
        localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
      }
    } catch (e) {
      console.warn('[ThemeService] Could not persist pending preview.', e);
    }
    this.pendingPreviewSubject.next(false);
  }

  /**
   * Drop the in-flight preview and reload the last-saved template + customs.
   */
  discardPendingPreview(): void {
    if (!this.pendingPreviewSubject.value) return;
    this.pendingCustomPreset = null;
    this.customOverrides = this.getSavedCustomOverrides();
    this.activePresetId = this.getSavedActivePresetId();
    const savedName = this.getSavedThemeName() || 'default';
    this.init(savedName);
    this.pendingPreviewSubject.next(false);
  }

  /** Apply + persist custom overrides to localStorage. */
  saveCustomOverrides(overrides: DeepPartial<Theme>): void {
    this.previewCustomOverrides(overrides);
    try {
      localStorage.setItem(STORAGE_KEY_CUSTOM_OVERRIDES, JSON.stringify(overrides));
    } catch (e) {
      console.warn('[ThemeService] Could not persist custom overrides.', e);
    }
  }

  /** Clear only the custom overrides layer (leaves the active template intact). */
  clearCustomOverrides(): void {
    this.customOverrides = null;
    this.activePresetId = null;
    this.pendingCustomPreset = null;
    try {
      localStorage.removeItem(STORAGE_KEY_CUSTOM_OVERRIDES);
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
    } catch {
      /* ignore */
    }
    this.applyGlobalTheme();
  }

  /**
   * Discard any preview-only changes and restore the last-saved state.
   * Useful when the user closes the selector without hitting Save.
   */
  cancelPreview(fallbackName = 'default'): void {
    this.pendingCustomPreset = null;
    const savedCustom = this.getSavedCustomOverrides();
    this.customOverrides = savedCustom;
    this.activePresetId = this.getSavedActivePresetId();
    const savedName = this.getSavedThemeName() || fallbackName;
    this.init(savedName);
  }

  /**
   * Wipe all persisted theme state and reload the base default.
   * Used by the Theme Selector's "Reset to Default" action.
   */
  resetToDefault(): void {
    try {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_THEME);
      localStorage.removeItem(STORAGE_KEY_CUSTOM_OVERRIDES);
      localStorage.removeItem(STORAGE_KEY_ACTIVE_PRESET_ID);
    } catch {
      /* ignore */
    }
    this.customOverrides = null;
    this.activePresetId = null;
    this.pendingCustomPreset = null;
    const defaultName = this.manifestCache?.defaultTheme || 'default';
    this.init(defaultName);
  }

  // ── Dashboard-Level Overrides ───────────────────────────────────

  /**
   * Apply dashboard-level theme overrides (from dashboard JSON config).
   * Merged on top of the centralized theme.
   */
  setDashboardOverrides(overrides: DeepPartial<Theme>): void {
    this.dashboardOverrides = overrides;
    this.applyGlobalTheme();
  }

  /**
   * Clear screen-level overrides when the owning screen is destroyed.
   * This ensures navigating between dashboards doesn't leak theme overrides.
   */
  clearDashboardOverrides(): void {
    this.dashboardOverrides = null;
    this.applyGlobalTheme();
  }

  // ── Theme Resolution ────────────────────────────────────────────

  /**
   * Resolve the effective theme by merging:
   *   centralTheme + dashboardOverrides + widgetOverrides
   *
   * Called by every chart/table component in its renderChart() method.
   *
   * @param widgetOverrides Optional widget-level overrides (from widget JSON)
   */
  resolve(widgetOverrides?: DeepPartial<Theme>): Theme {
    if (!this.centralTheme) {
      console.warn('[ThemeService] Theme not initialized. Call init() first.');
      return {} as Theme;
    }
    // Merge order (lowest → highest priority):
    //   centralTheme < dashboardOverrides < customOverrides < widgetOverrides
    // Custom (user) intentionally wins over dashboard so the Custom Theme tab
    // can override per-dashboard defaults. Widget-level overrides still win
    // because they're intentional per-widget configuration.
    return deepMerge(
      lockChartPalette(this.centralTheme)!,
      this.dashboardOverrides,
      this.customOverrides,
      widgetOverrides,
    );
  }

  /** Convenience: resolve only chart theme tokens. */
  resolveChartTheme(widgetOverrides?: DeepPartial<Theme>): ChartTheme {
    return this.resolve(widgetOverrides).chart;
  }

  /** Convenience: resolve only tooltip theme tokens. */
  resolveTooltipTheme(widgetOverrides?: DeepPartial<Theme>): TooltipTheme {
    return this.resolve(widgetOverrides).tooltip;
  }

  /** Convenience: resolve only table theme tokens. */
  resolveTableTheme(widgetOverrides?: DeepPartial<Theme>): TableTheme {
    return this.resolve(widgetOverrides).table;
  }

  // ── Apply (CSS Variables + ECharts) ─────────────────────────────

  /**
   * Apply central + dashboard theme as CSS custom properties on <html>
   * and register the ECharts theme. Also emits on theme$.
   */
  private applyGlobalTheme(): void {
    if (!this.centralTheme) return;
    // Merge order matches resolve(): user customs intentionally win over
    // dashboard config. Chart palette stays locked to the fixed default.
    const theme = deepMerge(
      lockChartPalette(this.centralTheme)!,
      this.dashboardOverrides,
      this.customOverrides,
    );
    const dark = isDarkTheme(theme);
    const targets = this.themeScope ? [this.themeScope] : [document.documentElement, document.body];
    for (const target of targets) {
      target.classList.toggle('dark-theme', dark);
      target.classList.toggle('dvm-dark-theme', dark);
    }
    this.applyThemeTokensTo(theme);
    this.registerEChartsTheme(theme);
    this.themeSubject.next(theme);
  }

  /**
   * Compute what the resolved theme WOULD be with the given custom overrides —
   * without mutating any state. Used by the Theme Selector's preview pane.
   */
  previewMerge(customOverrides?: DeepPartial<Theme>): Theme | null {
    if (!this.centralTheme) return null;
    return deepMerge(lockChartPalette(this.centralTheme)!, customOverrides);
  }

  /**
   * Set every --dvm-* CSS variable on the target element (default: <html>).
   * Public so the Theme Selector can scope the variables to its preview pane,
   * making a theme "preview" that only affects the dialog content.
   */
  applyThemeTokensTo(
    theme: Theme,
    target: HTMLElement = this.themeScope ?? document.documentElement,
  ): void {
    const set = (k: string, v: string | number) =>
      target.style.setProperty(k, String(v));

    // Typography — optional global font family (Custom Theme tab)
    if (theme.fontFamily) set('--dvm-font-family', theme.fontFamily);

    // Optional widget gap — consumed by screen grid styles as
    // `gap: var(--dvm-widget-gap, <jsonGap>px)` so this wins when set.
    if (theme.widgetGap) set('--dvm-widget-gap', theme.widgetGap);

    // Global page heading typography. Optional for backward compatibility
    // with existing theme files; CSS supplies the default values when absent.
    if (theme.pageHeader?.titleSize) set('--dvm-page-title-size', theme.pageHeader.titleSize);
    else target.style.removeProperty('--dvm-page-title-size');
    if (theme.pageHeader?.titleWeight) set('--dvm-page-title-weight', theme.pageHeader.titleWeight);
    else target.style.removeProperty('--dvm-page-title-weight');
    if (theme.pageHeader?.titleColor) set('--dvm-page-title-color', theme.pageHeader.titleColor);
    else target.style.removeProperty('--dvm-page-title-color');
    if (theme.pageHeader?.titleLetterSpacing) set('--dvm-page-title-letter-spacing', theme.pageHeader.titleLetterSpacing);
    else target.style.removeProperty('--dvm-page-title-letter-spacing');
    if (theme.pageHeader?.titleMarginBottom) set('--dvm-page-title-margin-bottom', theme.pageHeader.titleMarginBottom);
    else target.style.removeProperty('--dvm-page-title-margin-bottom');
    if (theme.pageHeader?.subtitleSize) set('--dvm-page-subtitle-size', theme.pageHeader.subtitleSize);
    else target.style.removeProperty('--dvm-page-subtitle-size');
    if (theme.pageHeader?.subtitleWeight) set('--dvm-page-subtitle-weight', theme.pageHeader.subtitleWeight);
    else target.style.removeProperty('--dvm-page-subtitle-weight');
    if (theme.pageHeader?.subtitleColor) set('--dvm-page-subtitle-color', theme.pageHeader.subtitleColor);
    else target.style.removeProperty('--dvm-page-subtitle-color');

    // Optional loading-overlay scrim (per-card spinners). Conditional emit so
    // light themes that don't opt in fall through to the SCSS rgba(255,...,0.8)
    // fallback — preserves today's look. Explicit removeProperty on switch
    // back so a previous dark theme's value doesn't linger.
    if (theme.overlayBg) {
      set('--dvm-overlay-bg', theme.overlayBg);
    } else {
      target.style.removeProperty('--dvm-overlay-bg');
    }

    // Layout — Sidebar
    set('--dvm-sidebar-bg', theme.layout.sidebar.bg);
    set('--dvm-sidebar-text', theme.layout.sidebar.text);
    set('--dvm-sidebar-hover-bg', theme.layout.sidebar.hoverBg);
    set('--dvm-sidebar-active-bg', theme.layout.sidebar.activeBg);
    set('--dvm-sidebar-border-color', theme.layout.sidebar.borderColor);

    // Layout — Toolbar
    set('--dvm-toolbar-bg', theme.layout.toolbar.bg);
    set('--dvm-toolbar-text', theme.layout.toolbar.text);
    set('--dvm-toolbar-shadow', theme.layout.toolbar.shadow);

    // Layout — Content
    set('--dvm-content-bg', theme.layout.content.bg);
    set('--dvm-content-text', theme.layout.content.text);
    set('--dvm-content-heading-text', theme.layout.content.headingText);
    // Optional dashboard background image (Custom Theme tab upload).
    // Stored as a CSS `url(...)` string — pass `none` when unset so styles.scss
    // can use the var directly without an additional fallback.
    set('--dvm-content-bg-image', theme.layout.content.bgImage || 'none');

    // Card
    set('--dvm-card-bg', theme.card.bg);
    set('--dvm-card-header-bg', theme.card.headerBg);
    set('--dvm-card-header-text', theme.card.headerText);
    set('--dvm-card-body-bg', theme.card.bodyBg);
    set('--dvm-card-border-color', theme.card.borderColor);
    set('--dvm-card-shadow', theme.card.shadow);
    set('--dvm-card-hover-shadow', theme.card.hoverShadow);
    // Optional card corner radius (Custom Theme tab). Falls back to the
    // default 8px in styles.scss if unset.
    if (theme.card.borderRadius) set('--dvm-card-border-radius', theme.card.borderRadius);
    // Optional card body horizontal padding ("Card padding" density choice).
    if (theme.card.padding) set('--dvm-card-body-padding', theme.card.padding);

    // Table
    set('--dvm-table-header-bg', theme.table.headerBg);
    set('--dvm-table-header-text', theme.table.headerText);
    set('--dvm-table-row-bg', theme.table.rowBg);
    set('--dvm-table-alt-row-bg', theme.table.altRowBg);
    set('--dvm-table-hover-bg', theme.table.hoverBg);
    set('--dvm-table-border-color', theme.table.borderColor);
    set('--dvm-table-common-bg', theme.table.commonBg);
    set('--dvm-table-non-common-bg', theme.table.nonCommonBg);
    set('--dvm-table-frozen-bg', theme.table.frozenBg);
    set('--dvm-table-frozen-border-color', theme.table.frozenBorderColor);
    // Optional cell/header selection + hover + resize-handle + collapsed-col
    // tokens — emit-or-clear so a previous theme's values don't linger after
    // a switch. Light themes that don't opt in fall through to the SCSS
    // fallbacks (the original hardcoded slate-blue / white-line look).
    const optionalTableTokens: Array<[keyof TableTheme, string]> = [
      ['selectedBg', '--dvm-table-selected-bg'],
      ['selectedText', '--dvm-table-selected-text'],
      ['selectedHoverBg', '--dvm-table-selected-hover-bg'],
      ['cellHoverBg', '--dvm-table-cell-hover-bg'],
      ['resizeHandleBg', '--dvm-table-resize-handle-bg'],
      ['resizeHandleLine', '--dvm-table-resize-handle-line'],
      ['resizeHandleLineHover', '--dvm-table-resize-handle-line-hover'],
      ['collapsedBg', '--dvm-table-collapsed-bg'],
      ['collapsedHoverBg', '--dvm-table-collapsed-hover-bg'],
    ];
    for (const [key, cssVar] of optionalTableTokens) {
      const value = theme.table[key];
      if (typeof value === 'string' && value) {
        set(cssVar, value);
      } else {
        target.style.removeProperty(cssVar);
      }
    }

    // Filter
    set('--dvm-filter-bg', theme.filter.bg);
    set('--dvm-filter-border-color', theme.filter.borderColor);
    set('--dvm-filter-text', theme.filter.text);
    set('--dvm-filter-icon-color', theme.filter.iconColor);
    // Optional dropdown-option hover/selected overrides — only emit when
    // the theme provides them, so non-opting themes keep Material defaults.
    // Explicitly remove on theme switch so a previous theme's value doesn't
    // linger after switching back to a non-opting one.
    if (theme.filter.optionHoverBg) {
      set('--dvm-filter-option-hover-bg', theme.filter.optionHoverBg);
    } else {
      target.style.removeProperty('--dvm-filter-option-hover-bg');
    }
    if (theme.filter.optionHoverText) {
      set('--dvm-filter-option-hover-text', theme.filter.optionHoverText);
    } else {
      target.style.removeProperty('--dvm-filter-option-hover-text');
    }

    // Stat Card
    set('--dvm-stat-card-bg', theme.statCard.bg);
    set('--dvm-stat-card-title-color', theme.statCard.titleColor);
    set('--dvm-stat-card-value-color', theme.statCard.valueColor);
    set('--dvm-stat-card-border-color', theme.statCard.borderColor);
    set('--dvm-stat-card-shadow', theme.statCard.shadow);

    // Legend
    set('--dvm-legend-text-color', theme.legend.textColor);
    set('--dvm-legend-border-color', theme.legend.borderColor);
    set('--dvm-legend-hover-bg', theme.legend.hoverBg);
    set('--dvm-legend-active-bg', theme.legend.activeBg);
    set('--dvm-legend-arrow-color', theme.legend.arrowColor);

    // Dialog
    set('--dvm-dialog-bg', theme.dialog.bg);
    set('--dvm-dialog-header-bg', theme.dialog.headerBg);
    set('--dvm-dialog-header-text', theme.dialog.headerText);
    set('--dvm-dialog-body-text', theme.dialog.bodyText);
    set('--dvm-dialog-border-color', theme.dialog.borderColor);
    set('--dvm-dialog-confirm-btn-bg', theme.dialog.confirmBtnBg);
    set('--dvm-dialog-confirm-btn-text', theme.dialog.confirmBtnText);
    set('--dvm-dialog-cancel-btn-bg', theme.dialog.cancelBtnBg);
    set('--dvm-dialog-cancel-btn-text', theme.dialog.cancelBtnText);

    // Form
    set('--dvm-form-bg', theme.form.bg);
    set('--dvm-form-label-color', theme.form.labelColor);
    set('--dvm-form-input-bg', theme.form.inputBg);
    set('--dvm-form-input-border', theme.form.inputBorder);
    set('--dvm-form-input-text', theme.form.inputText);
    set('--dvm-form-focus-border', theme.form.focusBorder);
    set('--dvm-form-error-color', theme.form.errorColor);
    set('--dvm-form-placeholder-color', theme.form.placeholderColor);

    // Button
    set('--dvm-btn-primary-bg', theme.button.primaryBg);
    set('--dvm-btn-primary-text', theme.button.primaryText);
    set('--dvm-btn-primary-hover-bg', theme.button.primaryHoverBg);
    set('--dvm-btn-secondary-bg', theme.button.secondaryBg);
    set('--dvm-btn-secondary-text', theme.button.secondaryText);
    set('--dvm-btn-disabled-bg', theme.button.disabledBg);
    // Optional button corner radius — drives both the action-button radius
    // (--dvm-btn-border-radius) and the filter-button radius
    // (--dvm-filter-radius) so the chosen shape is consistent.
    if (theme.button.borderRadius) {
      set('--dvm-btn-border-radius', theme.button.borderRadius);
      set('--dvm-filter-radius', theme.button.borderRadius);
    }

    // Scrollbar
    set('--dvm-scrollbar-track-bg', theme.scrollbar.trackBg);
    set('--dvm-scrollbar-thumb-bg', theme.scrollbar.thumbBg);
    set('--dvm-scrollbar-border-color', theme.scrollbar.borderColor);
  }

  // ── ECharts Theme Registration ──────────────────────────────────

  /**
   * Register a custom ECharts theme named 'dvm'.
   * Chart components use it via: echarts.init(chartDom, 'dvm').
   *
   * Registered theme provides baseline colors — individual setOption() calls
   * still take highest priority (data-specific colors still work).
   */
  private registerEChartsTheme(theme: Theme): void {
    const echartsTheme: any = {
      color: theme.chart.palette,
      backgroundColor: theme.chart.bg,
      textStyle: { color: theme.chart.textColor },
      title: {
        textStyle: { color: theme.chart.textColor },
        subtextStyle: { color: theme.chart.axisLabelColor },
      },
      categoryAxis: {
        axisLine: { show: true, lineStyle: { color: theme.chart.axisLineColor } },
        axisTick: { show: true, lineStyle: { color: theme.chart.axisLineColor } },
        // Capital Case formatting for category axis labels (UI Standards Point 4).
        // Numbers and special values (e.g. Q1) pass through unchanged via formatValueSafely.
        axisLabel: {
          show: true,
          color: theme.chart.axisLabelColor,
          formatter: (value: any) => formatValueSafely(value, 'capital'),
        },
        splitLine: { show: false, lineStyle: { color: [theme.chart.splitLineColor] } },
      },
      valueAxis: {
        axisLine: { show: false, lineStyle: { color: theme.chart.axisLineColor } },
        axisTick: { show: false, lineStyle: { color: theme.chart.axisLineColor } },
        axisLabel: { show: true, color: theme.chart.axisLabelColor },
        splitLine: { show: true, lineStyle: { color: [theme.chart.splitLineColor] } },
      },
      legend: {
        textStyle: { color: theme.chart.legendTextColor },
        // Capital Case formatting for legend item names (UI Standards Point 4).
        formatter: (name: string) => formatValueSafely(name, 'capital'),
      },
      tooltip: {
        backgroundColor: theme.tooltip.bg,
        borderColor: theme.tooltip.borderColor,
        textStyle: {
          color: theme.tooltip.textColor,
          fontSize: theme.tooltip.fontSize,
        },
      },
    };

    echarts.registerTheme('dvm', echartsTheme);
  }
}
